import AddIcon from '@mui/icons-material/Add';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import PetsIcon from '@mui/icons-material/Pets';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import PetProfile from 'happypaws-shared/src/Pets/models/PetProfile';
import { PetType } from 'happypaws-shared/src/Pets/models/PetType';
import { AgeType, Gender } from 'happypaws-shared/src/Pets/models/common/CommonAnswersPets';
import React from 'react';

import CatWithFish from '../../../../assets/images/cat-and-fish.png';
import OtherCat from '../../../../assets/images/cat-other.png';
import Huskey from '../../../../assets/images/husky.png';

// ── Constants ────────────────────────────────────────────────────────────────

export const petTypeSelection = [
  { type: PetType.DOG, label: 'Dog', animal: Huskey },
  { type: PetType.CAT, label: 'Cat', animal: CatWithFish },
  { type: PetType.OTHER, label: 'Other', animal: OtherCat },
];

export const dogBreeds = [
  'Golden Retriever',
  'Labrador Retriever',
  'German Shepherd',
  'Bulldog',
  'Beagle',
  'Poodle',
  'Rottweiler',
  'Yorkshire Terrier',
  'Boxer',
  'Dachshund',
  'Siberian Husky',
  'Chihuahua',
];

export const catBreeds = [
  'Persian',
  'Maine Coon',
  'Siamese',
  'Ragdoll',
  'Bengal',
  'British Shorthair',
  'Sphynx',
  'Scottish Fold',
];

export const otherPetTypes = [
  'Bird',
  'Rabbit',
  'Hamster',
  'Guinea Pig',
  'Reptile',
  'Fish',
  'Ferret',
  'Turtle',
];

// ── ToggleButtonGroup ─────────────────────────────────────────────────────────

interface ToggleButtonGroupProps {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}

export const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
  options,
  value,
  onChange,
}) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
    {options.map((option) => (
      <Button
        key={option.value}
        variant={value === option.value ? 'contained' : 'outlined'}
        onClick={() => onChange(option.value)}
        sx={{
          flex: 1,
          minWidth: '100px',
          textTransform: 'none',
          borderRadius: 2,
          fontWeight: 600,
          borderColor: '#178B8E',
          color: value === option.value ? 'white' : '#178B8E',
          backgroundColor: value === option.value ? '#178B8E' : 'transparent',
          '&:hover': {
            backgroundColor: value === option.value ? '#0F6164' : 'rgba(23, 139, 142, 0.08)',
            borderColor: '#178B8E',
          },
        }}
      >
        {option.label}
      </Button>
    ))}
  </Box>
);

// ── PetSummaryRow ─────────────────────────────────────────────────────────────

interface PetSummaryRowProps {
  pet: PetProfile;
  index: number;
  isActive: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export const PetSummaryRow: React.FC<PetSummaryRowProps> = ({
  pet,
  index,
  isActive,
  onEdit,
  onDelete,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: isActive ? 'rgba(23, 139, 142, 0.08)' : '#fff',
      border: isActive ? '2px solid #178B8E' : '2px solid #E0E0E0',
      borderRadius: 2,
      p: 1.5,
      mb: 1,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: '#F5F5F5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {pet.photoUrl ? (
          <Box
            component="img"
            src={pet.photoUrl}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <PetsIcon sx={{ fontSize: 28, color: '#BDBDBD' }} />
        )}
      </Box>
      <Box>
        <Typography variant="body1" fontWeight={600}>
          {pet.petName || `Pet ${index + 1}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {pet.breed || pet.petSpecies || pet.type || ''}
          {pet.age ? ` · ${pet.age}` : ''}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={onEdit}
        sx={{
          textTransform: 'none',
          borderColor: '#178B8E',
          color: '#178B8E',
          '&:hover': { backgroundColor: 'rgba(23, 139, 142, 0.08)' },
        }}
      >
        Edit
      </Button>
      <Button
        size="small"
        variant="outlined"
        onClick={onDelete}
        sx={{
          textTransform: 'none',
          borderColor: '#E53935',
          color: '#E53935',
          '&:hover': { backgroundColor: 'rgba(229, 57, 53, 0.08)' },
        }}
      >
        Delete
      </Button>
    </Box>
  </Box>
);

// ── Specific Fields ───────────────────────────────────────────────────────────

export interface SpecificFieldsProps {
  currentPet: PetProfile;
  updateCurrentPet: (updates: Partial<PetProfile>) => void;
}

export const DogSpecific = React.memo(({ currentPet, updateCurrentPet }: SpecificFieldsProps) => (
  <>
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Age of your pet:
      </Typography>
      <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#5A5A5A' }}>
        If you want to be more specific, feel free to enter your pet's exact age in years below!
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button
            variant={currentPet.ageType === AgeType.PUPPY ? 'contained' : 'outlined'}
            onClick={() => updateCurrentPet({ ageType: AgeType.PUPPY })}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#178B8E',
              color: currentPet.ageType === AgeType.PUPPY ? 'white' : '#178B8E',
              backgroundColor: currentPet.ageType === AgeType.PUPPY ? '#178B8E' : 'transparent',
            }}
          >
            Puppy
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button
            variant={currentPet.ageType === AgeType.ADULT ? 'contained' : 'outlined'}
            onClick={() => updateCurrentPet({ ageType: AgeType.ADULT })}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#178B8E',
              color: currentPet.ageType === AgeType.ADULT ? 'white' : '#178B8E',
              backgroundColor: currentPet.ageType === AgeType.ADULT ? '#178B8E' : 'transparent',
            }}
          >
            Adult
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            placeholder="Write age here..."
            value={currentPet.age ?? ''}
            onChange={(e) => updateCurrentPet({ ageType: AgeType.CUSTOM, age: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Grid>
      </Grid>
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Gender of your pet:
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button
            variant={currentPet.gender === Gender.MALE ? 'contained' : 'outlined'}
            onClick={() => updateCurrentPet({ gender: Gender.MALE })}
            startIcon={<MaleIcon />}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#42A5F5',
              color: currentPet.gender === Gender.MALE ? 'white' : '#42A5F5',
              backgroundColor: currentPet.gender === Gender.MALE ? '#42A5F5' : 'transparent',
            }}
          >
            Male
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button
            variant={currentPet.gender === Gender.FEMALE ? 'contained' : 'outlined'}
            onClick={() => updateCurrentPet({ gender: Gender.FEMALE })}
            startIcon={<FemaleIcon />}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#EC407A',
              color: currentPet.gender === Gender.FEMALE ? 'white' : '#EC407A',
              backgroundColor: currentPet.gender === Gender.FEMALE ? '#EC407A' : 'transparent',
            }}
          >
            Female
          </Button>
        </Grid>
      </Grid>
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        House trained:
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: 'Depends', value: 'depends' },
        ]}
        value={currentPet.houseTrained || ''}
        onChange={(val) => updateCurrentPet({ houseTrained: val as any })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Friendly with children:
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: 'Depends', value: 'depends' },
        ]}
        value={currentPet.friendlyWithChildren || ''}
        onChange={(val) => updateCurrentPet({ friendlyWithChildren: val as any })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Friendly with dogs:
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: 'Depends', value: 'depends' },
        ]}
        value={currentPet.friendlyWithDogs || ''}
        onChange={(val) => updateCurrentPet({ friendlyWithDogs: val as any })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Friendly with cats:
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: 'Depends', value: 'depends' },
        ]}
        value={currentPet.friendlyWithCats || ''}
        onChange={(val) => updateCurrentPet({ friendlyWithCats: val as any })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Energy level:
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: 'High', value: 'high' },
          { label: 'Moderate', value: 'moderate' },
          { label: 'Low', value: 'low' },
        ]}
        value={currentPet.energyLevel || ''}
        onChange={(val) => updateCurrentPet({ energyLevel: val as any })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Can be left alone:
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: '< 1 hour', value: '< 1 hour' },
          { label: '1 - 4 hours', value: '1 - 4 hours' },
          { label: '4 hours >', value: '4 hours >' },
        ]}
        value={currentPet.canBeLeftAlone || ''}
        onChange={(val) => updateCurrentPet({ canBeLeftAlone: val })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Any additional information the sitter should know?:
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        placeholder="Instructions for walking, feeding, medicine or other..."
        value={currentPet.additionalInfo || ''}
        onChange={(e) => updateCurrentPet({ additionalInfo: e.target.value })}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
    </Box>
  </>
));

export const CatSpecific = React.memo(({ currentPet, updateCurrentPet }: SpecificFieldsProps) => (
  <>
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Gender of your pet:
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button
            variant={currentPet.gender === Gender.MALE ? 'contained' : 'outlined'}
            onClick={() => updateCurrentPet({ gender: Gender.MALE })}
            startIcon={<MaleIcon />}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#42A5F5',
              color: currentPet.gender === Gender.MALE ? 'white' : '#42A5F5',
              backgroundColor: currentPet.gender === Gender.MALE ? '#42A5F5' : 'transparent',
            }}
          >
            Male
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button
            variant={currentPet.gender === Gender.FEMALE ? 'contained' : 'outlined'}
            onClick={() => updateCurrentPet({ gender: Gender.FEMALE })}
            startIcon={<FemaleIcon />}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#EC407A',
              color: currentPet.gender === Gender.FEMALE ? 'white' : '#EC407A',
              backgroundColor: currentPet.gender === Gender.FEMALE ? '#EC407A' : 'transparent',
            }}
          >
            Female
          </Button>
        </Grid>
      </Grid>
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Age:
      </Typography>
      <TextField
        fullWidth
        placeholder="Write age here..."
        value={currentPet.age || ''}
        onChange={(e) => updateCurrentPet({ age: e.target.value })}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Litter trained:
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: 'Depends', value: 'depends' },
        ]}
        value={currentPet.litterTrained || ''}
        onChange={(val) => updateCurrentPet({ litterTrained: val as any })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Indoor or Outdoor?
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: 'Indoor', value: 'indoor' },
          { label: 'Outdoor', value: 'outdoor' },
          { label: 'Both', value: 'both' },
        ]}
        value={currentPet.indoorOutdoor || ''}
        onChange={(val) => updateCurrentPet({ indoorOutdoor: val as any })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Declawed?
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ]}
        value={currentPet.declawed || ''}
        onChange={(val) => updateCurrentPet({ declawed: val as any })}
      />
    </Box>

    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Can be left alone:
      </Typography>
      <ToggleButtonGroup
        options={[
          { label: '< 1 hour', value: '< 1 hour' },
          { label: '4-8 hours', value: '4-8 hours' },
          { label: '8+ hours', value: '8+ hours' },
        ]}
        value={currentPet.canBeLeftAlone || ''}
        onChange={(val) => updateCurrentPet({ canBeLeftAlone: val })}
      />
    </Box>
  </>
));

export const OtherPetsSpecific = React.memo(
  ({ currentPet, updateCurrentPet }: SpecificFieldsProps) => (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Breed/Species:
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g., Parakeet, Ball Python, etc."
          value={currentPet.breed || ''}
          onChange={(e) => updateCurrentPet({ breed: e.target.value })}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Age:
        </Typography>
        <TextField
          fullWidth
          placeholder="Write age here..."
          value={currentPet.age || ''}
          onChange={(e) => updateCurrentPet({ age: e.target.value })}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Habitat Type:
        </Typography>
        <TextField
          fullWidth
          placeholder="Cage, Tank, Free-roam, etc."
          value={currentPet.habitatType || ''}
          onChange={(e) => updateCurrentPet({ habitatType: e.target.value })}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Can be handled?
        </Typography>
        <ToggleButtonGroup
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
            { label: 'With care', value: 'withCare' },
          ]}
          value={currentPet.canBeHandled || ''}
          onChange={(val) => updateCurrentPet({ canBeHandled: val as any })}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Special care requirements:
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Feeding, temperature, cleaning, etc."
          value={currentPet.specialCare || ''}
          onChange={(e) => updateCurrentPet({ specialCare: e.target.value })}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>
    </>
  )
);

// ── PetFormBody ───────────────────────────────────────────────────────────────
// Shared form fields used by both Add and Edit pages

interface PetFormBodyProps {
  currentPet: PetProfile;
  updateCurrentPet: (updates: Partial<PetProfile>) => void;
  mainPhotoInputRef: React.RefObject<HTMLInputElement | null>;
  galleryInputRefs: React.RefObject<(HTMLInputElement | null)[]>;
  onMainPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onGalleryPhotoUpload: (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  petTypeFields: React.ReactNode;
  onDeleteMainPhoto?: () => void;
  onDeleteGalleryPhoto?: (index: number) => void;
}

export const PetFormBody: React.FC<PetFormBodyProps> = ({
  currentPet,
  updateCurrentPet,
  mainPhotoInputRef,
  galleryInputRefs,
  onMainPhotoUpload,
  onGalleryPhotoUpload,
  petTypeFields,
  onDeleteMainPhoto,
  onDeleteGalleryPhoto,
}) => (
  <>
    {/* Pet Name */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Pet name:
      </Typography>
      <TextField
        fullWidth
        placeholder="Write your pet name"
        value={currentPet.petName ?? ''}
        onChange={(e) => updateCurrentPet({ petName: e.target.value })}
        required
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
    </Box>

    {/* Breed / Species */}
    {currentPet.type !== PetType.OTHER ? (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Breed:
          </Typography>
          <FormControl fullWidth>
            <Select
              value={currentPet.breed || ''}
              onChange={(e) => updateCurrentPet({ breed: e.target.value })}
              displayEmpty
              disabled={currentPet.isMixBreed}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="" disabled>
                Select breed
              </MenuItem>
              {(currentPet.type === PetType.DOG ? dogBreeds : catBreeds).map((breed) => (
                <MenuItem key={breed} value={breed}>
                  {breed}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {currentPet.type === PetType.DOG && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentPet.isMixBreed ?? false}
                  onChange={(e) =>
                    updateCurrentPet({
                      isMixBreed: e.target.checked,
                      breed: e.target.checked ? 'Mix Breed' : '',
                    })
                  }
                  sx={{ color: '#178B8E', '&.Mui-checked': { color: '#178B8E' } }}
                />
              }
              label="Mix Breed"
            />
          )}
        </Grid>
      </Grid>
    ) : (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Pet Type:
        </Typography>
        <FormControl fullWidth>
          <Select
            value={currentPet.petSpecies || ''}
            onChange={(e) => updateCurrentPet({ petSpecies: e.target.value })}
            displayEmpty
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="" disabled>
              Select pet type
            </MenuItem>
            {otherPetTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    )}

    {/* Description */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Description:
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        placeholder="Write a description of your pet..."
        value={currentPet.description || ''}
        onChange={(e) => updateCurrentPet({ description: e.target.value })}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
    </Box>

    {/* Main Photo */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Photo of your pet:
      </Typography>
      <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#5A5A5A' }}>
        Please make sure to upload clear and high-quality photos of your pet so we can get to know
        them better!
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <input
            ref={mainPhotoInputRef}
            type="file"
            accept="image/*"
            onChange={onMainPhotoUpload}
            style={{ display: 'none' }}
          />
          <Button
            variant="contained"
            startIcon={<CameraAltIcon />}
            onClick={() => mainPhotoInputRef.current?.click()}
            fullWidth
            sx={{
              backgroundColor: '#178B8E',
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': { backgroundColor: '#0F6164' },
            }}
          >
            Upload Pet Photo
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper
            sx={{
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #E0E0E0',
              backgroundColor: '#F5F5F5',
              position: 'relative', // ADD
            }}
          >
            {currentPet.photoUrl ? (
              <>
                <Box
                  component="img"
                  src={currentPet.photoUrl}
                  sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
                {onDeleteMainPhoto && (
                  <IconButton
                    size="small"
                    onClick={onDeleteMainPhoto}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      width: 26,
                      height: 26,
                      '&:hover': { backgroundColor: 'rgba(211,47,47,0.8)' },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </>
            ) : (
              <PetsIcon sx={{ fontSize: 48, color: '#BDBDBD' }} />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>

    {/* Type-specific fields */}
    {petTypeFields}

    {/* Photo Gallery */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Photo gallery:
      </Typography>
      <Grid container spacing={2}>
        {[0, 1, 2, 3].map((index) => (
          <Grid size={{ xs: 6, sm: 3 }} key={index}>
            <input
              ref={(el) => {
                galleryInputRefs.current[index] = el;
              }}
              type="file"
              accept="image/*"
              onChange={onGalleryPhotoUpload(index)}
              style={{ display: 'none' }}
            />
            <Paper
              onClick={() => galleryInputRefs.current[index]?.click()}
              sx={{
                height: 120,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #E0E0E0',
                backgroundColor: '#F5F5F5',
                cursor: 'pointer',
                position: 'relative', // ADD
                '&:hover': { borderColor: '#178B8E', backgroundColor: 'rgba(23, 139, 142, 0.05)' },
              }}
            >
              {(currentPet.photoGallery ?? [])[index] ? (
                <>
                  <Box
                    component="img"
                    src={(currentPet.photoGallery ?? [])[index] as string}
                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                  {onDeleteGalleryPhoto && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteGalleryPhoto(index);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        width: 26,
                        height: 26,
                        '&:hover': { backgroundColor: 'rgba(211,47,47,0.8)' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </>
              ) : index === 0 ? (
                <>
                  <AddIcon sx={{ fontSize: 32, color: '#BDBDBD' }} />
                  <Typography variant="caption" sx={{ color: '#5A5A5A' }}>
                    Add Photos
                  </Typography>
                </>
              ) : (
                <PetsIcon sx={{ fontSize: 48, color: '#BDBDBD' }} />
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  </>
);
